import React from "react";
import styles from "./ContactSection.module.css";
import { FaTerminal } from "react-icons/fa6";
import { MdKeyboardArrowRight } from "react-icons/md";
import Button from "../../Button/Button";
import { useNavigate } from "react-router-dom";
import { navigateToMeeting } from "../../../utils/navigation";
import { isSmallScreen } from "../../../utils/screen";
import { useTranslation } from "../../../hooks/useTranslation";

const ContactSection: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const cards = [
    {
      title: t("contact.card1.title"),
      description: t("contact.card1.description"),
      buttonText: t("contact.card1.button"),
      style: "meetingCard",
      action: "meeting"
    },
    {
      title: t("contact.card2.title"),
      description: t("contact.card2.description"),
      buttonText: t("contact.card2.button"),
      style: "callCard",
      action: "call"
    },
    {
      title: t("contact.card3.title"),
      description: t("contact.card3.description"),
      buttonText: t("contact.card3.button"),
      style: "estimateCard",
      action: "calculator"
    },
  ];

  const handleCardAction = (action: string) => {
    switch (action) {
      case "meeting":
        // Navigate to contact page and scroll to calendar
        navigateToMeeting(navigate);
        break;
      case "call":
        // Show phone number modal - assuming the function is in the window object
        if (typeof window !== 'undefined' && (window as any).toggleDialog) {
          (window as any).toggleDialog();
        }
        break;
      case "calculator":
        // Navigate to calculator page
        navigate("/calculator");
        break;
    }
  };

  return (
    <section className={styles.contactSection}>
      {/* Section Header */}
      <div className={styles.titleContainer}>
        <FaTerminal size={isSmallScreen() ? 44 : 54} />
        <div className={styles.text}>
          <h2 className={styles.sectionTitle}>
            {t("contact.title")}
          </h2>
          <p className={styles.sectionSubtitle}>
            {t("contact.subtitle")}
          </p>
        </div>
      </div>

      {/* Grid Container */}
      <div className={styles.cardsContainer}>
        {/* Left Column: Single Card */}
        <div
          className={`${styles.card} ${styles[cards[0].style]}`}
          onClick={() => handleCardAction(cards[0].action)}
          role="button"
          tabIndex={0}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleCardAction(cards[0].action);
            }
          }}
        >
          <div>
            <h3 className={styles.cardTitle}>{cards[0].title}</h3>
            <p className={styles.cardDescription}>{cards[0].description}</p>
          </div>
          <Button
            variant={"text-underline"}
            fullWidth={true}
            className={styles.cardButton}
            rightIcon={<MdKeyboardArrowRight size={32} />}
            onClick={() => handleCardAction(cards[0].action)}
          >
            {cards[0].buttonText}
          </Button>
        </div>

        {/* Right Column: Two Cards */}
        <div className={styles.rightColumn}>
          {cards.slice(1).map((card, index) => (
            <div
              key={index}
              className={`${styles.card} ${styles[card.style]}`}
              onClick={() => handleCardAction(card.action)}
              role="button"
              tabIndex={0}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleCardAction(card.action);
                }
              }}
            >
              <div>
                <h3 className={styles.cardTitle}>{card.title}</h3>
                <p className={styles.cardDescription}>{card.description}</p>
              </div>
              <Button
                variant={"text-underline"}
                fullWidth={true}
                className={styles.cardButton}
                rightIcon={<MdKeyboardArrowRight size={32} />}
                onClick={() => handleCardAction(card.action)}
              >
                {card.buttonText}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
